/*
 * DTOs consumidos pelo painel. Espelham as respostas da API.
 * Mantidos aqui (e nao em @hotspot/shared) enquanto sao usados apenas pelo painel.
 */

export interface UnitDto {
  id: number;
  name: string;
  slug: string;
  city: string | null;
  state: string | null;
  address: string | null;
  timezone: string;
  isActive: boolean;
  mikrotikCount: number;
  createdAt: string;
}

export type MikrotikStatusValue = 'online' | 'offline' | 'unknown';
export type MikrotikAccessModeValue = 'public_ip' | 'vpn';

export interface MikrotikDto {
  id: number;
  name: string;
  unitId: number | null;
  unitName: string | null;
  accessMode: MikrotikAccessModeValue;
  host: string;
  port: number;
  driver: string;
  useTls: boolean;
  tlsTrusted: boolean;
  tlsFingerprintPreview: string | null;
  username: string;
  hotspotServerName: string | null;
  portalSlug: string;
  vpnAddress: string | null;
  hasVpnKey: boolean;
  status: MikrotikStatusValue;
  lastSeenAt: string | null;
  lastCheckedAt: string | null;
  lastError: string | null;
  lastLatencyMs: number | null;
  routerosVersion: string | null;
  boardName: string | null;
  identity: string | null;
  uptimeSeconds: number | null;
  isActive: boolean;
  createdAt: string;
}

export interface HotspotServerDto {
  id: string;
  name: string;
  interface: string | null;
  profile: string | null;
  addressPool: string | null;
  disabled: boolean;
}

export interface ConnectionTestDto {
  ok: boolean;
  latencyMs: number;
  errorCode: string | null;
  errorMessage: string | null;
  errorDetail: string | null;
  observedFingerprint: string | null;
  requiresFingerprintConfirmation: boolean;
  router: {
    version: string;
    boardName: string;
    identity: string;
    uptimeSeconds: number;
    cpuLoad: number | null;
    freeMemoryBytes: number | null;
    totalMemoryBytes: number | null;
  } | null;
  hotspotServers: HotspotServerDto[];
}

export interface ProvisionStepDto {
  step: 'certificate' | 'www-ssl' | 'reverse-proxy' | 'fingerprint';
  status: 'done' | 'skipped' | 'failed';
  detail: string;
}

export interface ProvisionResultDto {
  ok: boolean;
  httpsPort: number;
  fingerprint: string | null;
  fingerprintTrusted: boolean;
  steps: ProvisionStepDto[];
}

export interface RouterInterfaceDto {
  id: string;
  name: string;
  type: string;
  running: boolean;
  disabled: boolean;
  comment: string | null;
}

export interface NetworkContextDto {
  interfaces: RouterInterfaceDto[];
  bridges: string[];
  managementInterface: string | null;
  wanInterface: string | null;
  bridgePorts: { interfaceName: string; bridge: string }[];
  interfacesWithAddress: string[];
}

export interface HotspotPlanSummaryDto {
  gatewayAddress: string;
  poolRange: string;
  network: string;
  bridgeName: string;
  hotspotName: string;
}

export interface HotspotStepDto {
  id: string;
  title: string;
  status: 'done' | 'skipped' | 'failed' | 'not-run';
  detail: string;
}

export interface HotspotProvisionDto {
  ok: boolean;
  summary: HotspotPlanSummaryDto | null;
  validationIssues: { field: string; issue: string }[];
  steps: HotspotStepDto[];
}

export interface HotspotScriptDto {
  script: string;
  summary: HotspotPlanSummaryDto;
}

export interface PortalProvisionDto {
  ok: boolean;
  portalUrl: string;
  portalHost: string;
  portalIps: string[];
  steps: { id: string; title: string; status: string; detail: string }[];
}

export interface VpnScriptDto {
  tunnelAddress: string;
  publicKey: string;
  routerScript: string;
  serverPeerSnippet: string;
}

export interface FleetSummaryDto {
  total: number;
  online: number;
  offline: number;
  unknown: number;
}

/** Segundos para "3d 4h 5m", como o operador espera ler. */
export function formatUptime(seconds: number | null): string {
  if (seconds === null || seconds <= 0) return '—';
  const dias = Math.floor(seconds / 86_400);
  const horas = Math.floor((seconds % 86_400) / 3_600);
  const minutos = Math.floor((seconds % 3_600) / 60);
  if (dias > 0) return `${dias}d ${horas}h`;
  if (horas > 0) return `${horas}h ${minutos}m`;
  return `${minutos}m`;
}

/* ---- Campanhas ----------------------------------------------------------- */

export interface CampaignDto {
  id: number;
  title: string;
  description: string | null;
  startsAt: string | null;
  endsAt: string | null;
  priority: number;
  unitId: number | null;
  unitName: string | null;
  isActive: boolean;
  isRunning: boolean;
  adCount: number;
  mikrotikIds: number[];
  createdAt: string;
}

export interface AdDto {
  id: number;
  campaignId: number;
  mediaId: number;
  mediaUrl: string;
  width: number | null;
  height: number | null;
  position: number;
  linkUrl: string | null;
  ctaLabel: string | null;
  minViewSeconds: number;
  allowSkip: boolean;
  isRequired: boolean;
  isActive: boolean;
}

export interface MediaUploadDto {
  id: number;
  url: string;
  width: number | null;
  height: number | null;
}

/* ---- Aparencia do portal -------------------------------------------------- */

export interface PortalThemeEffective {
  logoUrl: string | null;
  backgroundColor: string;
  cardColor: string;
  textColor: string;
  mutedColor: string;
  buttonColor: string;
  buttonTextColor: string;
  title: string;
  subtitle: string;
  buttonLabel: string;
  footerText: string;
  successTitle: string;
  successMessage: string;
}

export interface PortalThemeDto {
  id: number | null;
  mikrotikId: number;
  logoMediaId?: number | null;
  logoUrl: string | null;
  backgroundColor?: string | null;
  cardColor?: string | null;
  textColor?: string | null;
  mutedColor?: string | null;
  buttonColor?: string | null;
  buttonTextColor?: string | null;
  title?: string | null;
  subtitle?: string | null;
  buttonLabel?: string | null;
  footerText?: string | null;
  successTitle?: string | null;
  successMessage?: string | null;
  effective: PortalThemeEffective;
}

/* ---- Configuracao de acesso (modo de autenticacao) ------------------------ */

export interface HotspotConfigEffective {
  authMode: string;
  /** Nulo = sem limite de tempo. */
  sessionTimeoutMinutes: number | null;
  idleTimeoutMinutes: number;
  rateLimit: string | null;
  redirectUrl: string | null;
  revisitGraceDays: number;
}

export interface HotspotConfigDto {
  id: number | null;
  mikrotikId: number;
  authMode: string | null;
  sessionTimeoutMinutes: number | null;
  idleTimeoutMinutes: number | null;
  rateLimit: string | null;
  redirectUrl: string | null;
  revisitGraceDays: number | null;
  /** O gravado, com o padrao de fabrica no lugar dos campos em branco. */
  effective: HotspotConfigEffective;
}

/* ---- Integracoes externas ------------------------------------------------ */

export type DecisaoDeStatus = 'allow' | 'deny';

export interface StatusVistoDto {
  status: string;
  /** Como o operador classificou. Nulo = sem opiniao, a presenca decide. */
  decisao: DecisaoDeStatus | null;
  /** O que o sistema chutaria pelo nome do status, so como dica. */
  sugestao: DecisaoDeStatus | null;
}

export interface IntegrationSettingsDto {
  configurado: boolean;
  baseUrl: string;
  authType: string;
  clientId: string | null;
  username: string | null;
  /** A API nunca devolve o segredo, so se ele existe. */
  temClientSecret: boolean;
  temPassword: boolean;
  timeoutMs: number;
  cacheTtlSeconds: number;
  statusMap: Record<string, DecisaoDeStatus>;
  statusVistos: StatusVistoDto[];
  isActive: boolean;
  lastCheckedAt: string | null;
  lastCheckOk: boolean | null;
  lastCheckError: string | null;
}

/* ---- Termos e politica de privacidade ------------------------------------ */

export interface TermDto {
  id: number;
  kind: string;
  version: string;
  title: string;
  bodyMd: string;
  isActive: boolean;
  publishedAt: string | null;
  /** Quantos clientes ja aceitaram esta versao. */
  acceptanceCount: number;
  createdAt: string;
}

/* ---- Clientes -------------------------------------------------------------- */

export interface CustomerDto {
  id: number;
  /** Nulo depois da anonimizacao. */
  name: string | null;
  phone: string | null;
  accessCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
  consentMarketing: boolean;
  isBlocked: boolean;
  blockedReason: string | null;
  isAnonymized: boolean;
  deviceCount: number;
  /** Onde acessou por ultimo. Nulo se nunca abriu sessao. */
  lastMikrotikId: number | null;
  lastMikrotikName: string | null;
  /** VLAN por onde acessou por ultimo. Nulo se nunca abriu sessao. */
  lastVlan: string | null;
}

export interface CustomerDetailDto extends CustomerDto {
  devices: {
    id: number;
    mac: string;
    isRandomizedMac: boolean;
    isBlocked: boolean;
    firstSeenAt: string;
    lastSeenAt: string;
  }[];
  sessions: {
    id: number;
    mikrotikName: string | null;
    status: string;
    authMethod: string;
    ip: string | null;
    vlan: string | null;
    startedAt: string;
    endedAt: string | null;
  }[];
  acceptances: {
    id: number;
    kind: string;
    version: string;
    title: string;
    acceptedAt: string;
  }[];
}

/* ---- Sessoes de hotspot ---------------------------------------------------- */

export interface LiveSessionDto {
  /** Identificador NO ROTEADOR: muda a cada login. */
  routerId: string;
  mac: string | null;
  ip: string | null;
  user: string | null;
  loginBy: string | null;
  uptimeSeconds: number;
  idleSeconds: number;
  bytesIn: number;
  bytesOut: number;
  /** VLAN ou interface por onde o aparelho chegou. */
  vlan: string | null;
  deviceId: number | null;
  deviceBlocked: boolean;
  isRandomizedMac: boolean;
  customerId: number | null;
  customerName: string | null;
  /** Falso quando o acesso nao passou pelo portal deste sistema. */
  conhecido: boolean;
}

export interface SessionHistoryDto {
  id: number;
  mac: string;
  ip: string | null;
  status: string;
  authMethod: string;
  customerName: string | null;
  startedAt: string;
  endedAt: string | null;
}

/** Bytes em algo que se le de relance: 1,4 MB. */
export function formatarBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const unidades = ['KB', 'MB', 'GB', 'TB'];
  let valor = bytes / 1024;
  let indice = 0;
  while (valor >= 1024 && indice < unidades.length - 1) {
    valor /= 1024;
    indice += 1;
  }
  return `${valor.toFixed(valor >= 10 ? 0 : 1).replace('.', ',')} ${unidades[indice]}`;
}

/** Segundos para "2h 14m" ou "45s". */
export function formatarDuracao(segundos: number): string {
  if (segundos < 60) return `${segundos}s`;
  const horas = Math.floor(segundos / 3600);
  const minutos = Math.floor((segundos % 3600) / 60);
  if (horas === 0) return `${minutos}m`;
  return `${horas}h ${minutos}m`;
}

/* ---- Relatorios ------------------------------------------------------------ */

export interface DashboardDto {
  resumo: {
    acessosHoje: number;
    acessosOntem: number;
    dispositivosHoje: number;
    clientesNovosHoje: number;
    clientesTotal: number;
    acessosNaJanela: number;
    equipamentosOnline: number;
    equipamentosTotal: number;
  };
  porDia: { dia: string; total: number; liberados: number }[];
  porEquipamento: { rotulo: string; total: number }[];
  porModo: { rotulo: string; total: number }[];
  /** `rotulo` e a VLAN crua; nulo = acesso sem VLAN registrada. */
  porLocalidade: { rotulo: string | null; total: number }[];
  /** Toda VLAN ja vista, para o filtro. */
  localidades: { mikrotikId: number; vlan: string }[];
}

export interface CampaignMetricDto {
  campaignId: number;
  title: string;
  isActive: boolean;
  impressoes: number;
  concluidas: number;
  cliques: number;
  pulos: number;
  taxaDeClique: number;
  taxaDeConclusao: number;
}

/* ---- Auditoria ------------------------------------------------------------- */

export interface AuditEntryDto {
  id: number;
  action: string;
  entityType: string;
  entityId: string | null;
  actorType: string;
  actorLabel: string | null;
  ip: string | null;
  before: unknown;
  after: unknown;
  createdAt: string;
}

export interface AuditOptionsDto {
  actions: string[];
  entityTypes: string[];
  actors: { id: number; label: string }[];
}

/* --- Painel gerencial ------------------------------------------------------ */

export interface UsageLocalidadeDto {
  mikrotikId: number;
  mikrotikName: string;
  vlan: string | null;
  acessos: number;
  liberados: number;
  dispositivos: number;
  bytesDown: number;
  bytesUp: number;
  picoSimultaneos: number;
  mediaSimultaneos: number;
}

export interface UsageReportDto {
  periodo: { de: string; ate: string };
  resumo: {
    acessos: number;
    liberados: number;
    dispositivos: number;
    anterior: { acessos: number; dispositivos: number };
    bytesDown: number;
    bytesUp: number;
    picoSimultaneos: number;
    picoEm: string | null;
    mediaSimultaneos: number;
    /** Antes desta data nao ha trafego nem conectados registrados. */
    coletaDesde: string | null;
  };
  porDia: {
    dia: string;
    acessos: number;
    liberados: number;
    dispositivos: number;
    bytesDown: number;
    bytesUp: number;
    picoSimultaneos: number;
  }[];
  porLocalidade: UsageLocalidadeDto[];
  /** diaDaSemana: 1 = segunda ... 7 = domingo. */
  porHorario: { diaDaSemana: number; hora: number; acessos: number; mediaSimultaneos: number }[];
  localidades: { mikrotikId: number; vlan: string }[];
}

export interface OnlineNowDto {
  mikrotikId: number;
  mikrotikName: string;
  /** Falso: o equipamento nao respondeu, e o zero quer dizer "nao sei". */
  ok: boolean;
  total: number;
  localidades: { vlan: string | null; online: number }[];
}

/**
 * Nome legivel de uma VLAN: "vlan3602-Jardim-Primavera" vira "Jardim Primavera".
 * Sem padrao reconhecivel, devolve o nome como veio do roteador.
 */
export function nomeDaLocalidade(vlan: string | null): string {
  if (!vlan) return 'Não identificada';
  const nome = vlan
    .replace(/^vlan[\s_-]*\d*[\s_-]*/i, '')
    .replace(/[-_]+/g, ' ')
    .trim();
  return nome || vlan;
}

/* ---- Chamados ------------------------------------------------------------- */

export interface TicketPessoaDto {
  id: number;
  name: string;
}

export interface TicketListItemDto {
  id: number;
  title: string;
  category: string;
  priority: string;
  status: string;
  createdBy: TicketPessoaDto | null;
  /** Onde e o problema. Nulo quando o chamado nao e de um lugar. */
  local: { mikrotikId: number; mikrotikName: string | null; vlan: string | null } | null;
  sla: TicketSlaDto;
  createdAt: string;
  lastMessageAt: string;
  /** Para quem atende: espera resposta. Para quem abriu: resposta nao lida. */
  precisaAtencao: boolean;
}

export interface TicketMessageDto {
  id: number;
  /** 'message' publica, 'note' interna, 'event' mudanca de situacao. */
  kind: 'message' | 'note' | 'event';
  body: string;
  author: TicketPessoaDto | null;
  doSuporte: boolean;
  createdAt: string;
  attachments: { id: number; fileName: string; width: number | null; height: number | null }[];
}

export interface TicketDetailDto extends TicketListItemDto {
  closedAt: string | null;
  podeReabrir: boolean;
  messages: TicketMessageDto[];
}

export interface EstadoDoPrazoDto {
  alvoMin: number;
  /** Ainda correndo: quando vence. Nulo se ja acabou. */
  venceEm: string | null;
  /** Tempo de SLA gasto quando ja foi atendido. */
  segundos: number | null;
  /** true cumprido, false estourado, null no prazo ou nao se aplica. */
  cumprido: boolean | null;
}

export interface TicketSlaDto {
  resposta: EstadoDoPrazoDto;
  solucao: EstadoDoPrazoDto;
  /** Pendente: relogio parado. */
  pausado: boolean;
  /** Re-chamado: o chamado anterior no mesmo ponto. */
  reChamadoDe: number | null;
  /** Segundos do prazo de solucao ja consumidos pelos anteriores. */
  herdadoSeg: number;
}

export type SlaConfigDto = Record<
  'urgente' | 'alta' | 'normal' | 'baixa' | 'trocaDeEquipamento',
  { respostaMin: number; solucaoMin: number }
> & {
  pausarEmPendente: boolean;
  /** Zero desliga o re-chamado. */
  reChamadoHoras: number;
};

export interface TicketMetricasDto {
  total: number;
  emAberto: number;
  finalizados: number;
  cancelados: number;
  atrasados: number;
  reChamados: number;
  mediaRespostaSeg: number | null;
  mediaSolucaoSeg: number | null;
  respostaNoPrazo: number;
  respostaForaDoPrazo: number;
  solucaoNoPrazo: number;
  solucaoForaDoPrazo: number;
}

export interface TicketReportDto {
  periodo: { de: string; ate: string };
  resumo: TicketMetricasDto;
  porPrioridade: (TicketMetricasDto & { prioridade: string })[];
  porLocal: (TicketMetricasDto & {
    mikrotikId: number | null;
    mikrotikName: string | null;
    vlan: string | null;
  })[];
  porCategoria: { categoria: string; total: number }[];
  porDia: { dia: string; abertos: number; finalizados: number }[];
}

/** Equipamento que o usuario pode indicar no chamado, com as pracas dele. */
export interface TicketLocationDto {
  mikrotikId: number;
  name: string;
  vlans: string[];
}

export interface TicketCountersDto {
  paraAtender: number;
  respostasNaoLidas: number;
}
